/**
 * Shared presentational building blocks for the three template-management
 * sections on the Operator Console (Contract, Owner Contract, Invoice).
 *
 * These are purely visual — they hold no data-fetching or mutation logic — so
 * every section renders with an identical, consistent look. Behaviour lives in
 * the individual section components.
 */
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { usePaywall } from "@/components/paywall/PaywallProvider";
import { useLanguage } from "@/contexts/LanguageContext";
import { StoredFileLink } from "@/components/shared/StoredFileLink";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Star,
  ExternalLink,
  Download,
  ChevronDown,
  Tags,
  type LucideIcon,
} from "lucide-react";

// ── Section shell (Card + revamped header + Add button) ──────────────
export function TemplateSectionCard({
  icon: Icon,
  title,
  description,
  onAdd,
  addLabel = "Add Template",
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  onAdd: () => void;
  addLabel?: string;
  children: ReactNode;
}) {
  // One chokepoint for every template section (contract / owner / invoice):
  // unpaid operators get the paywall instead of the create form (COL-352).
  const { guardCreate } = usePaywall();
  return (
    <Card className="overflow-hidden border-border/70 shadow-sm">
      <CardHeader className="flex flex-row items-start justify-between gap-4 border-b bg-muted/30 py-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <div className="space-y-1">
            <h3 className="text-base font-semibold leading-none tracking-tight">
              {title}
            </h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <Button
          onClick={() => guardCreate("create_template", onAdd)}
          size="sm"
          className="shrink-0 shadow-sm"
        >
          <Plus className="mr-1 h-4 w-4" />
          {addLabel}
        </Button>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

// ── Loading spinner ──────────────────────────────────────────────────
export function TemplateLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────
export function TemplateEmptyState({
  icon: Icon,
  title,
  hint,
}: {
  icon: LucideIcon;
  title: string;
  hint?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed bg-muted/20 py-12 text-center">
      <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="h-6 w-6" />
      </span>
      <p className="text-sm font-medium">{title}</p>
      {hint && (
        <p className="mt-1 max-w-md text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

// ── Default badge ────────────────────────────────────────────────────
export function DefaultBadge() {
  return (
    <Badge
      variant="outline"
      className="gap-1 border-amber-300/70 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400"
    >
      <Star className="h-3 w-3 fill-amber-400 text-amber-500" />
      Default
    </Badge>
  );
}

// ── Row action cluster (View / Download / Edit / Delete) ─────────────
export function TemplateRowActions({
  pdfUrl,
  downloadName,
  onEdit,
  onDelete,
}: {
  pdfUrl?: string | null;
  downloadName: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useLanguage();
  const openFailed = t("admin.docs.openFailed", "Could not open the document.");
  const pill =
    "inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground";
  return (
    <div className="flex items-center justify-end gap-1">
      {/* A stored public URL into the contracts bucket, which is going
          private: both links sign on click (COL-436). */}
      {pdfUrl && (
        <>
          <StoredFileLink
            value={pdfUrl}
            bucket="contracts"
            errorText={openFailed}
            className={pill}
          >
            <ExternalLink className="h-3.5 w-3.5" /> View
          </StoredFileLink>
          <StoredFileLink
            value={pdfUrl}
            bucket="contracts"
            download={downloadName}
            errorText={openFailed}
            className={pill}
          >
            <Download className="h-3.5 w-3.5" /> Download
          </StoredFileLink>
        </>
      )}
      <Button variant="ghost" size="icon" onClick={onEdit}>
        <Pencil className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" onClick={onDelete}>
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );
}

// ── Field-name reference panel (collapsed by default) ────────────────
export function TemplateFieldReference({
  title,
  description,
  fields,
}: {
  title: string;
  description: ReactNode;
  fields: readonly string[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-6 rounded-xl border bg-muted/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-xl px-4 py-3 text-left transition-colors hover:bg-muted/40"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <Tags className="h-4 w-4 text-muted-foreground" />
          {title}
          <span className="text-xs font-normal text-muted-foreground">
            {fields.length} fields
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="border-t px-4 pb-4 pt-3">
          <p className="mb-3 text-xs text-muted-foreground">{description}</p>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {fields.map((f) => (
              <code
                key={f}
                className="rounded-md bg-background/80 px-2 py-1 font-mono text-[11px] text-muted-foreground ring-1 ring-border/60"
              >
                {f}
              </code>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
